import "mapbox-gl/dist/mapbox-gl.css";
import "./Map.css";
import "@mapbox/mapbox-gl-geocoder/dist/mapbox-gl-geocoder.css";
import Box from '@mui/material/Box';
import React, {useState} from "react";
import mapboxgl from "!mapbox-gl"; // eslint-disable-line import/no-webpack-loader-syntax
import MapboxGeocoder  from "@mapbox/mapbox-gl-geocoder";
import {Comp} from "../model/objects";
import {DEFAULT_ZOOM, MAPBOX_TOKEN, STYLE_SATELLITE, STYLE_STREETS} from "../mapbox";

export const Map = ({
    initMap, height, basemap, onMove, withGeocoder, onGeocodeResult, withRefresh, onRefresh, withSatellite=true,
    withNavigation=false, tools=[], ...options
    }) => {
    const [mapStyle, setMapStyle] = useState(basemap||STYLE_STREETS);

    React.useEffect(() => {
        mapboxgl.accessToken = MAPBOX_TOKEN;
        const map = new mapboxgl.Map({
            container: 'map',
            style: `mapbox://styles/mapbox/${mapStyle}`,
            height: height || "100%",
            ...(options || {})
        });
        window.map = map;
        map.dragRotate.disable();
        map.touchZoomRotate.disableRotation();

        if (onMove) {
            map.on("movestart", () => onMove(true));
            map.on("moveend", () => onMove(false));
        }

        if (withNavigation) {
            map.addControl(new mapboxgl.NavigationControl({
                showCompass: false
            }), "top-right");
        }

        for (const tool of tools) {
            map.addControl(tool.control, tool.position || 'top-right');
        }

        if (withGeocoder) {
            const geocoder = new MapboxGeocoder({
                accessToken: mapboxgl.accessToken,
                mapboxgl: mapboxgl,
                marker: false,
                flyTo: false,
                countries: 'ca',
                types: 'address,place',
                placeholder: "Find a place"
            });
            if (onGeocodeResult) geocoder.on("result", onGeocodeResult);
            map.addControl(geocoder);
        }

        if (withSatellite) {
            map.addControl(new SatelliteControl(mapStyle, setMapStyle), "top-left");
        }
        if (withRefresh) {
            map.addControl(new RefreshControl(onRefresh), "top-left");
        }

        if (initMap) {
            initMap(map);
        }
        return () => { map.remove() }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    return (<Box id="map" sx={{
        width: "100%",
        height: "100%",
        borderRadius: "4px"
    }}/>)
};

export const MapWithMarker = ({geo, onGeoChange, zoom, ...props}) => {
    const [map, setMap] = React.useState(null);
    const [mark, setMark] = React.useState(null);

    const initMap = m => {
      setMap(m);
      if (props.initMap) {
          props.initMap(m);
      }
    };

    React.useEffect(() => {
        const newMark = (gp, map) => {
            let marker = new mapboxgl.Marker({draggable: true})
                .setLngLat(Comp.point(gp))
                .addTo(map);
            marker.on('dragend', () => {
                if (onGeoChange) onGeoChange(marker.getLngLat());
            });
            return marker;
        };


        if (map) {
            const hasMark = !!mark;
            if (hasMark) {
                mark.remove()
            }
            if (geo) {
                setMark(newMark(geo, map));

                const dest = {center: Comp.point(geo), curve: 4};
                if (!hasMark) {
                    map.flyTo({
                        ...dest, zoom: DEFAULT_ZOOM
                    });
                }
                else {
                    map.easeTo(dest);
                }
            }
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [geo, map]);

    const mapOpts = {...props};
    if (geo) {
        mapOpts.zoom = DEFAULT_ZOOM+2;
        mapOpts.center = Comp.point(geo)
    }
    else {
        mapOpts.zoom = zoom || DEFAULT_ZOOM;
    }

    return (<Map {...mapOpts} initMap={initMap}/>);
};

export class SatelliteControl {
    constructor(style, setStyle) {
        this.origStyle = style;
        this.style = style;
        this.setStyle = setStyle;
    }

    onAdd(map){
        this.map = map;

        const button = document.createElement("button");
        button.className = "mapbox-gl-draw_ctrl-draw-btn satellite-ctrl";
        if (this.style === STYLE_SATELLITE) {
            button.className += " selected";
        }
        button.style.setProperty("background-image", 'url(' + SatelliteIcon + ')');
        button.title = "Toggle Satellite Map";
        button.addEventListener("click", evt => {
            evt.stopPropagation();
            evt.preventDefault();

            const s = this.style === STYLE_SATELLITE ? this.origStyle : STYLE_SATELLITE;
            map.setStyle(`mapbox://styles/mapbox/${s}`);
            this.setStyle(s);
            this.style = s;
            button.classList.toggle("selected");

            setTimeout(() => {
                map.fire("stylechange");
            }, 1000);
        });

        this.container = document.createElement("div");
        this.container.className = "mapboxgl-ctrl-group mapboxgl-ctrl";
        this.container.appendChild(button);

        return this.container;
    }

    onRemove(){
        this.container.parentNode.removeChild(this.container);
        this.map = undefined;
        this.marker = undefined;
    }
}

export class RefreshControl {
    constructor(onRefresh) {
        this.onRefresh = onRefresh;
    }

    onAdd(map){
        this.map = map;

        const button = document.createElement("button");
        button.className = "mapbox-gl-draw_ctrl-draw-btn refresh-ctrl";

        button.style.setProperty("background-image", 'url(' + RefreshIcon + ')');
        button.title = "Refresh";
        button.addEventListener("click", evt => {
            evt.stopPropagation();
            evt.preventDefault();

            this.onRefresh(map);
        });

        this.container = document.createElement("div");
        this.container.className = "mapboxgl-ctrl-group mapboxgl-ctrl";
        this.container.appendChild(button);

        return this.container;
    }

    onRemove(){
        this.container.parentNode.removeChild(this.container);
        this.map = undefined;
        this.marker = undefined;
    }
}

export const MarkerIcon = "data:image/svg+xml;base64,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";
export const SatelliteIcon = "data:image/svg+xml;base64,PHN2ZyB4bWxucz0iaHR0cDovL3d3dy53My5vcmcvMjAwMC9zdmciIHhtbG5zOnhsaW5rPSJodHRwOi8vd3d3LnczLm9yZy8xOTk5L3hsaW5rIiB3aWR0aD0iMjAiIGhlaWdodD0iMjAiIHByZXNlcnZlQXNwZWN0UmF0aW89InhNaWRZTWlkIG1lZXQiIHZpZXdCb3g9IjAgMCA1MTIgNTEyIiBzdHlsZT0iLW1zLXRyYW5zZm9ybTogcm90YXRlKDM2MGRlZyk7IC13ZWJraXQtdHJhbnNmb3JtOiByb3RhdGUoMzYwZGVnKTsgdHJhbnNmb3JtOiByb3RhdGUoMzYwZGVnKTsiPjxwYXRoIGZpbGw9IiM2MjYyNjIiIGQ9Ik0xNiA0OTZoNDgwVjE2SDE2em00NDgtMzJoLTI1LjM3M2wtMTA0LTEwNGw1NC45MTItNTQuOTExTDQ2NCAzNzkuNTV6TTQ4IDQ4aDQxNnYyODYuM2wtNzQuNDYxLTc0LjQ2MUwzMTIgMzM3LjM3M2wtMTEyLTExMmwtMTUyIDE1MnptMCAzNzQuNjI3bDE1Mi0xNTJMMzkzLjM3MyA0NjRINDh6Ii8+PHBhdGggZmlsbD0iIzYyNjI2MiIgZD0iTTEyMCA4MEg4MHY0MGE0MCA0MCAwIDAgMCA0MC00MHoiLz48cGF0aCBmaWxsPSIjNjI2MjYyIiBkPSJNODAgMTYzLjY2MVYxOTYuNkExNTIuNDY4IDE1Mi40NjggMCAwIDAgMTk2LjYgODBoLTMyLjkzOUExMjAuNDcxIDEyMC40NzEgMCAwIDEgODAgMTYzLjY2MXoiLz48cmVjdCB4PSIwIiB5PSIwIiB3aWR0aD0iNTEyIiBoZWlnaHQ9IjUxMiIgZmlsbD0icmdiYSgwLCAwLCAwLCAwKSIgLz48L3N2Zz4K";
export const RefreshIcon = "data:image/svg+xml;base64,PHN2ZyBlbmFibGUtYmFja2dyb3VuZD0ibmV3IDAgMCAzMiAzMiIgdmlld0JveD0iMCAwIDMyIDMyIiB4bWxucz0iaHR0cDovL3d3dy53My5vcmcvMjAwMC9zdmciPgogICAgPGcgZmlsbD0icmdiKDAsMCwwKSI+CiAgICAgICAgPHBhdGggZD0ibTMuOTk5IDE4Ljc4NmMuMyAwIC41OTctLjEzNC43OTMtLjM5bC40MzUtLjU2NWMuNjUxIDMuODYyIDMuMzU2IDcuMTY2IDcuMTk2IDguNDk5IDEuMTgyLjQxIDIuMzk1LjYwNyAzLjU5Mi42MDcgMy41ODQgMCA3LjAzMy0xLjc2OCA5LjA5OC00Ljg4MS4zMDUtLjQ2LjE3OS0xLjA4MS0uMjgxLTEuMzg2LS40NjItLjMwNy0xLjA4LS4xNzktMS4zODcuMjgxLTIuMjQ5IDMuMzk0LTYuNTEyIDQuODMtMTAuMzY1IDMuNDktMy4xOTctMS4xMS01LjQyNy0zLjg5My01LjkwMy03LjEyNWwuODU2LjY1OWMuNDM5LjMzNSAxLjA2Ni4yNTUgMS40MDItLjE4My4zMzctLjQzOC4yNTYtMS4wNjUtLjE4Mi0xLjQwMiAwIDAtMi4zNjUtMS44MjEtMi4zNzEtMS44MjUtLjQ5Ny0uMzgzLTEuMjA0LS41OTgtMS42NTQtLjAxMWwtMi4wMiAyLjYyM2MtLjMzNy40MzgtLjI1NiAxLjA2NS4xODIgMS40MDIuMTgxLjEzOS4zOTYuMjA3LjYwOS4yMDd6Ii8+CiAgICAgICAgPHBhdGggZD0ibTcuMTY5IDExLjMzMWMuNDU5LjMwNSAxLjA4LjE3OSAxLjM4Ny0uMjgxIDIuMjQ5LTMuMzkzIDYuNTE2LTQuODI5IDEwLjM2NS0zLjQ5IDMuMTk3IDEuMTEgNS40MjcgMy44OTMgNS45MDMgNy4xMjVsLS44NTYtLjY1OWMtLjQzOS0uMzM2LTEuMDY2LS4yNTYtMS40MDIuMTgzLS4zMzcuNDM4LS4yNTYgMS4wNjUuMTgyIDEuNDAyIDAgMCAyLjM2NSAxLjgyIDIuMzcxIDEuODI1LjUwNi4zODkgMS4xOTcuNjA3IDEuNjU0LjAxMWwyLjAyLTIuNjIzYy4zMzctLjQzOC4yNTYtMS4wNjUtLjE4Mi0xLjQwMnMtMS4wNjYtLjI1NS0xLjQwMi4xODNsLS40MzUuNTY1Yy0uNjUxLTMuODYyLTMuMzU2LTcuMTY2LTcuMTk2LTguNDk5LTQuNzItMS42MzgtOS45MzYuMTE5LTEyLjY5IDQuMjczLS4zMDUuNDYxLS4xNzkgMS4wODEuMjgxIDEuMzg3eiIvPgogICAgPC9nPgo8L3N2Zz4=";

export const MoveIcon = "data:image/svg+xml;base64,PHN2ZyB4bWxucz0iaHR0cDovL3d3dy53My5vcmcvMjAwMC9zdmciIGhlaWdodD0iMjQiIHZpZXdCb3g9IjAgLTk2MCA5NjAgOTYwIiB3aWR0aD0iMjQiPjxwYXRoIGQ9Ik00ODAtODAgMzEwLTI1MGw1Ny01NyA3MyA3M3YtMTY2aDgwdjE2NWw3Mi03MyA1OCA1OEw0ODAtODBaTTI1MC0zMTAgODAtNDgwbDE2OS0xNjkgNTcgNTctNzIgNzJoMTY2djgwSDIzNWw3MyA3Mi01OCA1OFptNDYwIDAtNTctNTcgNzMtNzNINTYwdi04MGgxNjVsLTczLTcyIDU4LTU4IDE3MCAxNzAtMTcwIDE3MFpNNDQwLTU2MHYtMTY2bC03MyA3My01Ny01NyAxNzAtMTcwIDE3MCAxNzAtNTcgNTctNzMtNzN2MTY2aC04MFoiLz48L3N2Zz4="

export const mapOptsForOrg = org => {
    const buffer = 0.001;
    const opts = {
        ...(org.settings||{}).map,
    };
    if (!opts.zoom || !opts.center) {
        opts.zoom = 3.3;
        opts.center = [-105.32, 54.21];
    }
    if (org.extent && org.extent.length === 4) {
        let [x1,y1,x2,y2] = org.extent;
        if ((x2 - x1)===0) {
            x1 -= buffer*10;
            x2 += buffer*10;
        }
        else {
            x1 -= buffer;
            x2 += buffer;
        }
        if ((y2 - y1)===0) {
            y1 -= buffer*10;
            y2 += buffer*10;
        }
        else {
            y1 -= buffer;
            y2 += buffer;
        }
        opts.bounds = [x1, y1, x2, y2];
    }
    return opts;
}
